import { useCallback, useMemo } from 'react';
import { atom, useAtom } from 'jotai';
import { useQuery } from '@tanstack/react-query';
import meetingService from '@/services/meeting/meeting-service';
import Storage from '@/utils/storage';
import * as Enums from '@/utils/enums';
import { DemoFromMeetings, DemoTiming, getDemoFromMeetings, getDemoTiming, isDemoBooked, resolveDemoBooking } from '@/PageComponents/Onboarding/onboarding-rules';
import { DemoBooking, readOnboardingStub, writeOnboardingStub } from '@/PageComponents/Onboarding/onboarding-stub-store';

/**
 * The company's demo, one copy for the whole app: the onboarding dashboard, the "Get set up quicker"
 * card (create job and elsewhere) and the integrations page all read it here, so a booking made in
 * any of them shows everywhere straight away.
 *
 * HubSpot's meetings (GET /Meeting/List) are shared through React Query's cache. A booking just
 * made in the iframe is shared through this atom (and kept in localStorage, see onboarding-stub-store)
 * until HubSpot's copy arrives.
 */

export const DEMO_MEETINGS_KEY = ['onboarding', 'meetings'];

// utils/Fetch resolves HTTP errors, so an unusable response is turned into a query error here.
export const fetchDemoMeetings = async () => {
    const response: any = await meetingService.getMeetings();
    if (!response || response.status >= 400 || !Array.isArray(response.Results)) throw new Error('meetings request failed');
    return response.Results;
};

const companyKey = () => String(Storage.getCookie(Enums.Cookie.tenantID) ?? 'unknown');

// Keyed by company, so switching tenant re-reads that company's stored booking.
const localBookingAtom = atom<{ companyKey: string; booking: DemoBooking | undefined } | null>(null);

export interface DemoBookingState {
    /** The demo as HubSpot reports it. */
    demo: DemoFromMeetings;
    /** The booking to show: HubSpot's, or one just made here while HubSpot catches up. */
    booking: DemoBooking | undefined;
    timing: DemoTiming;
    /** A demo is still ahead (see isDemoBooked). */
    booked: boolean;
    /** The meetings are still loading for the first time. */
    isLoading: boolean;
    /** Records a booking made in the iframe. */
    setLocalBooking: (booking: DemoBooking | undefined) => void;
}

export const useDemoBooking = (enabled = true): DemoBookingState => {
    const key = companyKey();
    const [local, setLocal] = useAtom(localBookingAtom);
    const localBooking = local?.companyKey === key ? local.booking : readOnboardingStub(key).demoBooking;

    const meetingsQuery = useQuery({ queryKey: DEMO_MEETINGS_KEY, queryFn: fetchDemoMeetings, enabled, retry: false });

    const setLocalBooking = useCallback((booking: DemoBooking | undefined) => {
        writeOnboardingStub(key, { demoBooking: booking });
        setLocal({ companyKey: key, booking });
    }, [key, setLocal]);

    const demo = useMemo(() => getDemoFromMeetings(meetingsQuery.data), [meetingsQuery.data]);
    const booking = resolveDemoBooking(meetingsQuery.data, demo, localBooking);
    const timing = getDemoTiming(booking);

    return {
        demo,
        booking,
        timing,
        booked: isDemoBooked(timing),
        isLoading: meetingsQuery.isLoading && meetingsQuery.fetchStatus !== 'idle',
        setLocalBooking,
    };
};
